/**
 * Outstanding Jerseys — admin view.
 *
 * Every player who paid for a jersey and has not been handed one, with the
 * parent contact and address needed to post it. Coaches work from their own
 * class roster; this page exists for the ones who miss a session.
 *
 * Roughly a quarter of parents have no address on file, so a missing
 * address is shown plainly rather than the row being hidden. Staff need to
 * see who they have to chase.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Shirt } from "lucide-react";
import toast from "react-hot-toast";

import DataTable from "../../components/admin/DataTable";
import FilterBar from "../../components/admin/FilterBar";
import Header from "../../components/Header";
import jerseyService from "../../api/services/jersey.service";
import {
  JERSEY_SIZE_OPTIONS,
  formatGrade,
  formatJerseySize,
} from "../../utils/format";
import {
  OUTSTANDING_CSV_HEADERS,
  outstandingCsvRow,
} from "./jerseyOutstandingCsv";

/** One list of sizes for the whole app; see utils/format. */
const formatSize = (size) => formatJerseySize(size) || "No size on file";

const formatAddress = (row) =>
  [row.address, row.city, row.state, row.zip_code].filter(Boolean).join(", ");

export default function JerseyOutstanding() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  // The row whose size is being saved, so one dropdown locks rather than all.
  const [savingId, setSavingId] = useState(null);

  const fetchOutstanding = useCallback(async () => {
    setLoading(true);
    try {
      const data = await jerseyService.getOutstanding();
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch (error) {
      console.error("Failed to load outstanding jerseys:", error);
      toast.error("Could not load outstanding jerseys. Please try again.");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOutstanding();
  }, [fetchOutstanding]);

  const filtered = useMemo(() => {
    const needle = searchQuery.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((row) =>
      [row.child_name, row.parent_name, row.parent_email, row.class_name]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(needle)),
    );
  }, [items, searchQuery]);

  // Sizes across everything still owed, so a bulk order can be placed.
  const sizeTotals = useMemo(() => {
    const counts = {};
    for (const row of filtered) {
      const key = row.jersey_size || "unknown";
      counts[key] = (counts[key] || 0) + 1;
    }
    return Object.entries(counts).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const handleExportCSV = () => {
    if (filtered.length === 0) {
      toast.error("Nothing to export");
      return;
    }

    const headers = OUTSTANDING_CSV_HEADERS;
    const rows = filtered.map((row) => outstandingCsvRow(row, formatSize));

    const csv = [headers, ...rows]
      .map((line) =>
        line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `outstanding-jerseys-${new Date().toISOString().split("T")[0]}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const changeSize = async (row, nextSize) => {
    const previous = row.jersey_size || "";
    if (nextSize === previous) return;

    try {
      setSavingId(row.enrollment_id);
      await jerseyService.overrideSize(row.enrollment_id, nextSize || null);

      // Update in place rather than refetching: a player with no size still
      // belongs on this list, so a reload would not remove the row, and
      // rebuilding the table would lose the scroll position mid-review.
      setItems((current) =>
        current.map((item) =>
          item.enrollment_id === row.enrollment_id
            ? { ...item, jersey_size: nextSize || null }
            : item,
        ),
      );

      toast.success(
        nextSize
          ? `${row.child_name || "Player"} set to ${formatJerseySize(nextSize)}.`
          : `Size cleared for ${row.child_name || "player"}.`,
      );
    } catch (error) {
      console.error("Could not change jersey size:", error);
      toast.error(
        error.response?.data?.message ||
          "Could not change that size. Please try again.",
      );
    } finally {
      setSavingId(null);
    }
  };

  const columns = [
    {
      key: "child_name",
      label: "Player",
      sortable: true,
      render: (value) => (
        <span className="font-semibold text-[#173151]">{value || "N/A"}</span>
      ),
    },
    {
      key: "grade",
      label: "Grade",
      sortable: true,
      render: (value) => formatGrade(value) || "—",
    },
    {
      key: "jersey_size",
      label: "Size",
      sortable: true,
      // Editable in place. The office spots a wrong size while working this
      // list, and sending them off to find the child record and come back
      // is how a wrong size ends up posted anyway. A coach cannot do this:
      // they raise a replacement request instead, which keeps the original
      // order on record. This is for a size simply entered wrong.
      render: (value, row) => (
        <select
          value={value || ""}
          disabled={savingId === row.enrollment_id}
          onChange={(e) => changeSize(row, e.target.value)}
          aria-label={`Jersey size for ${row.child_name || "this player"}`}
          className={`text-sm border rounded-lg px-2 py-1 bg-white font-manrope
            focus:outline-none focus:ring-2 focus:ring-[#173151] disabled:opacity-50
            ${value ? "border-gray-300 text-gray-700" : "border-amber-400 text-amber-700"}`}
        >
          <option value="">No size on file</option>
          {JERSEY_SIZE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "class_name",
      label: "Class",
      sortable: true,
      render: (value) => <span className="text-gray-700">{value || "N/A"}</span>,
    },
    {
      key: "parent_name",
      label: "Parent",
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="text-gray-800">{value || "N/A"}</p>
          <p className="text-xs text-gray-500">{row.parent_email || ""}</p>
        </div>
      ),
    },
    {
      key: "parent_phone",
      label: "Phone",
      render: (value) => (
        <span className="text-gray-700">{value || "—"}</span>
      ),
    },
    {
      key: "address",
      label: "Address",
      render: (value, row) =>
        value ? (
          <span className="text-gray-700">{formatAddress(row)}</span>
        ) : (
          <span className="text-amber-700 text-sm">No address on file</span>
        ),
    },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <Header />

      <div className="max-w-9xl mx-auto px-3 sm:px-4 py-4 flex-1 flex flex-col min-h-0 w-full">
        <div className="shrink-0 mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center items-start gap-3 sm:gap-4 justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-[30px] lg:text-[46px] font-bold text-text-primary font-kollektif truncate">
              Outstanding Jerseys
            </h1>
            <p className="text-xs sm:text-sm text-neutral-main font-manrope mt-1 hidden sm:block">
              Players who paid for a jersey and have not received one yet
            </p>
          </div>

          <div className="flex gap-2 sm:gap-4 shrink-0 items-center">
            <div className="bg-white rounded-lg border border-gray-200 p-2 sm:p-4 min-w-[80px] sm:min-w-[140px]">
              <p className="text-xs sm:text-sm text-gray-600 font-manrope">
                Still to hand out
              </p>
              <p className="text-lg sm:text-2xl font-bold text-[#173151] font-manrope mt-1">
                {total}
              </p>
            </div>
            <button
              type="button"
              onClick={handleExportCSV}
              className="flex items-center gap-2 bg-[#173151] hover:bg-[#1f3d67] text-white px-4 py-2.5 rounded-lg font-manrope font-semibold text-sm transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* A bulk order needs sizes, not names. */}
        {!loading && sizeTotals.length > 0 && (
          <div className="shrink-0 bg-white border border-gray-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Shirt className="w-4 h-4 text-[#173151]" />
              <h2 className="text-sm font-semibold text-[#173151] font-manrope">
                Sizes needed
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {sizeTotals.map(([size, count]) => (
                <span
                  key={size}
                  className="inline-flex items-center gap-2 bg-[#173151]/10 text-[#173151] text-sm font-medium font-manrope px-3 py-1.5 rounded-full"
                >
                  {size === "unknown" ? "No size on file" : formatSize(size)}
                  <strong>{count}</strong>
                </span>
              ))}
            </div>
          </div>
        )}

        <FilterBar
          onSearch={setSearchQuery}
          searchValue={searchQuery}
          searchPlaceholder="Player, parent, email or class..."
          onClearFilters={() => setSearchQuery("")}
          hasActiveFilters={Boolean(searchQuery)}
        />

        <div className="flex-1 min-h-0 overflow-auto">
          <DataTable
            columns={columns}
            data={filtered}
            loading={loading}
            totalItems={filtered.length}
            emptyMessage="Every player who bought a jersey has received one."
          />
        </div>
      </div>
    </div>
  );
}
